import React, { useState } from "react";
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  User,
  Users,
  Mail,
  Phone,
  Calendar,
  Award,
  CreditCard,
  FileText,
  AlertCircle,
  RefreshCw,
  QrCode,
  Send,
  Bell,
  History,
  Shield,
  Globe,
  Printer,
  Share2,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { Member, UnionHistoryEntry } from "../context/DataContext";
import { useToast } from "../context/ToastContext";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";

export default function Membership() {
  const { state, dispatch } = useData();
  const { showSuccess, showError, showInfo } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<
    "members" | "renewals" | "self-service"
  >("members");
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedRenewalMember, setSelectedRenewalMember] =
    useState<Member | null>(null);

  // Filter members based on search and status
  const filteredMembers = state.members.filter((member) => {
    const matchesSearch =
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      filterStatus === "all" || member.licenseStatus === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleAddMember = (memberData: Omit<Member, "id">) => {
    const newMember: Member = {
      ...memberData,
      id: Date.now().toString(),
    };
    dispatch({ type: "ADD_MEMBER", payload: newMember });
    setShowAddModal(false);
  };

  const handleUpdateMember = (updatedMember: Member) => {
    dispatch({ type: "UPDATE_MEMBER", payload: updatedMember });
    setShowMemberModal(false);
  };

  const handleDeleteMember = (memberId: string) => {
    if (window.confirm("Are you sure you want to delete this member?")) {
      dispatch({ type: "DELETE_MEMBER", payload: memberId });
    }
  };

  const handleRenewal = (memberId: string, newRenewalDate: string) => {
    const member = state.members.find((m) => m.id === memberId);
    if (member) {
      const updatedMember = {
        ...member,
        renewalDate: newRenewalDate,
        subscriptionStatus: "active" as const,
        lastPaymentDate: new Date().toISOString().split("T")[0],
        unionHistory: [
          ...member.unionHistory,
          {
            id: Date.now().toString(),
            date: new Date().toISOString().split("T")[0],
            action: "Renewal",
            description: "Membership renewed successfully",
          },
        ],
      };
      dispatch({ type: "UPDATE_MEMBER", payload: updatedMember });
      setShowRenewalModal(false);
      setSelectedRenewalMember(null);
    }
  };

  const sendRenewalReminder = (member: Member) => {
    // In a real app, this would send an email/SMS
    showSuccess(
      "Reminder Sent",
      `Renewal reminder sent to ${member.name} at ${member.email}`
    );
  };

  const generateDigitalIdCard = (member: Member) => {
    // In a real app, this would generate a PDF or QR code
    showSuccess(
      "ID Card Generated",
      `Digital ID card generated for ${member.name}`
    );
  };

  // Calculate renewal statistics
  const expiringSoon = state.members.filter((member) => {
    const renewalDate = new Date(member.renewalDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil(
      (renewalDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  });

  const overdueRenewals = state.members.filter((member) => {
    const renewalDate = new Date(member.renewalDate);
    const today = new Date();
    return renewalDate < today && member.subscriptionStatus !== "active";
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success">Active</Badge>;
      case "expired":
        return <Badge variant="error">Expired</Badge>;
      case "suspended":
        return <Badge variant="warning">Suspended</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getMembershipTypeBadge = (type: string) => {
    switch (type) {
      case "pilot":
        return <Badge variant="info">Pilot</Badge>;
      case "retired":
        return <Badge variant="default">Retired</Badge>;
      case "honorary":
        return <Badge variant="info">Honorary</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const columns = [
    {
      key: "name",
      header: "Member",
      render: (member: Member) => (
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
            <User className="h-5 w-5 text-primary-600" />
          </div>
          <div className="ml-3">
            <div className="text-sm font-medium text-gray-900">
              {member.name}
            </div>
            <div className="text-sm text-gray-500">{member.licenseNumber}</div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "Contact",
      render: (member: Member) => (
        <div>
          <div className="text-sm text-gray-900">{member.email}</div>
          <div className="text-sm text-gray-500">{member.phone}</div>
        </div>
      ),
    },
    {
      key: "membershipType",
      header: "Type",
      render: (member: Member) => getMembershipTypeBadge(member.membershipType),
    },
    {
      key: "licenseStatus",
      header: "Status",
      render: (member: Member) => getStatusBadge(member.licenseStatus),
    },
    {
      key: "renewalDate",
      header: "Renewal Date",
      render: (member: Member) =>
        format(new Date(member.renewalDate), "dd/MM/yy"),
    },
    {
      key: "actions",
      header: "Actions",
      render: (member: Member) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedMember(member);
              setShowMemberModal(true);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedMember(member);
              setShowMemberModal(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteMember(member.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Membership Management
          </h1>
          <p className="text-gray-600">
            Manage pilot members, licenses, and renewals
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Members</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.members.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <User className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Active Members
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  state.members.filter((m) => m.licenseStatus === "active")
                    .length
                }
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-gray-900">
                {expiringSoon.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Expired</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  state.members.filter((m) => m.licenseStatus === "expired")
                    .length
                }
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "members", name: "Members", icon: Users },
            { id: "renewals", name: "Renewals", icon: RefreshCw },
            { id: "self-service", name: "Self-Service Portal", icon: Globe },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "members" && (
        <>
          {/* Filters and Search */}
          <Card>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended</option>
                </select>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </Card>

          {/* Members Table */}
          <Card padding="none">
            <Table
              data={filteredMembers}
              columns={columns}
              onRowClick={(member) => {
                setSelectedMember(member);
                setShowMemberModal(true);
              }}
              emptyMessage="No members found"
            />
          </Card>
        </>
      )}

      {activeTab === "renewals" && (
        <div className="space-y-6">
          {/* Renewal Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Expiring Soon (30 days)
              </h3>
              <div className="space-y-3">
                {expiringSoon.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-sm text-gray-600">
                        Expires:{" "}
                        {format(new Date(member.renewalDate), "dd/MM/yy")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendRenewalReminder(member)}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedRenewalMember(member);
                          setShowRenewalModal(true);
                        }}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {expiringSoon.length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    No members expiring soon
                  </p>
                )}
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Overdue Renewals
              </h3>
              <div className="space-y-3">
                {overdueRenewals.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-sm text-gray-600">
                        Overdue since:{" "}
                        {format(new Date(member.renewalDate), "dd/MM/yy")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendRenewalReminder(member)}
                      >
                        <Bell className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedRenewalMember(member);
                          setShowRenewalModal(true);
                        }}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {overdueRenewals.length === 0 && (
                  <p className="text-gray-500 text-center py-4">
                    No overdue renewals
                  </p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "self-service" && (
        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Member Self-Service Portal
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <QrCode className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h4 className="font-semibold text-gray-900 mb-2">
                  Digital ID Card
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Generate and download your digital membership card
                </p>
                <Button variant="outline" size="sm">
                  <Printer className="h-4 w-4 mr-2" />
                  Generate Card
                </Button>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <CreditCard className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h4 className="font-semibold text-gray-900 mb-2">
                  Payment Portal
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Pay membership fees and view payment history
                </p>
                <Button variant="outline" size="sm">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Make Payment
                </Button>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <FileText className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h4 className="font-semibold text-gray-900 mb-2">
                  Document Access
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Access union documents and certificates
                </p>
                <Button variant="outline" size="sm">
                  <FileText className="h-4 w-4 mr-2" />
                  View Documents
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                variant="outline"
                className="h-20 flex flex-col items-center justify-center"
                onClick={() =>
                  showInfo(
                    "Update Profile",
                    "Update Profile functionality - This would open a profile update form"
                  )
                }
              >
                <Edit className="h-6 w-6 mb-2" />
                <span className="text-sm">Update Profile</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex flex-col items-center justify-center"
                onClick={() =>
                  showInfo(
                    "View History",
                    "View History functionality - This would show member activity history"
                  )
                }
              >
                <History className="h-6 w-6 mb-2" />
                <span className="text-sm">View History</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex flex-col items-center justify-center"
                onClick={() =>
                  showInfo(
                    "Share Contact",
                    "Share Contact functionality - This would generate a shareable contact card"
                  )
                }
              >
                <Share2 className="h-6 w-6 mb-2" />
                <span className="text-sm">Share Contact</span>
              </Button>
              <Button
                variant="outline"
                className="h-20 flex flex-col items-center justify-center"
                onClick={() =>
                  showInfo(
                    "Security Settings",
                    "Security Settings functionality - This would open security preferences"
                  )
                }
              >
                <Shield className="h-6 w-6 mb-2" />
                <span className="text-sm">Security Settings</span>
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Member Details Modal */}
      {selectedMember && (
        <MemberDetailsModal
          member={selectedMember}
          isOpen={showMemberModal}
          onClose={() => {
            setShowMemberModal(false);
            setSelectedMember(null);
          }}
          onUpdate={handleUpdateMember}
        />
      )}

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddMember}
      />

      {/* Renewal Modal */}
      {selectedRenewalMember && (
        <RenewalModal
          member={selectedRenewalMember}
          isOpen={showRenewalModal}
          onClose={() => {
            setShowRenewalModal(false);
            setSelectedRenewalMember(null);
          }}
          onRenew={handleRenewal}
        />
      )}
    </div>
  );
}

// Member Details Modal Component
function MemberDetailsModal({
  member,
  isOpen,
  onClose,
  onUpdate,
}: {
  member: Member;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (member: Member) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(member);

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Member Details" size="lg">
      <div className="space-y-6">
        {/* Digital ID Card */}
        <Card className="bg-gradient-to-r from-primary-500 to-primary-700 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                <User className="h-8 w-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{member.name}</h3>
                <p className="text-primary-100">
                  License: {member.licenseNumber}
                </p>
                <p className="text-primary-100">Member ID: {member.id}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-primary-100">
                Syndicate of Pilots
              </div>
              <div className="text-sm text-primary-100">Egypt</div>
            </div>
          </div>
        </Card>

        {/* Member Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">
              Personal Information
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                {isEditing ? (
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                ) : (
                  <p className="text-sm text-gray-900">{member.name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                {isEditing ? (
                  <Input
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                ) : (
                  <p className="text-sm text-gray-900">{member.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                {isEditing ? (
                  <Input
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                ) : (
                  <p className="text-sm text-gray-900">{member.phone}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">License Information</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  License Number
                </label>
                <p className="text-sm text-gray-900">{member.licenseNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <div className="mt-1">
                  {member.licenseStatus === "active" ? (
                    <Badge variant="success">Active</Badge>
                  ) : member.licenseStatus === "expired" ? (
                    <Badge variant="error">Expired</Badge>
                  ) : (
                    <Badge variant="warning">Suspended</Badge>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Membership Type
                </label>
                <div className="mt-1">
                  {member.membershipType === "pilot" ? (
                    <Badge variant="info">Pilot</Badge>
                  ) : member.membershipType === "retired" ? (
                    <Badge variant="default">Retired</Badge>
                  ) : (
                    <Badge variant="info">Honorary</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Qualifications */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3">Qualifications</h4>
          <div className="flex flex-wrap gap-2">
            {member.qualifications.map((qual, index) => (
              <Badge key={index} variant="info">
                {qual}
              </Badge>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Join Date
            </label>
            <p className="text-sm text-gray-900">
              {format(new Date(member.joinDate), "dd/MM/yy")}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Renewal Date
            </label>
            <p className="text-sm text-gray-900">
              {format(new Date(member.renewalDate), "dd/MM/yy")}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          {isEditing ? (
            <>
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button onClick={() => setIsEditing(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

// Add Member Modal Component
function AddMemberModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (member: Omit<Member, "id">) => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    licenseNumber: "",
    licenseStatus: "active" as const,
    membershipType: "pilot" as const,
    joinDate: new Date().toISOString().split("T")[0],
    renewalDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    qualifications: [] as string[],
    subscriptionStatus: "active" as const,
    unionHistory: [] as UnionHistoryEntry[],
  });

  const [newQualification, setNewQualification] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({
      name: "",
      email: "",
      phone: "",
      licenseNumber: "",
      licenseStatus: "active",
      membershipType: "pilot",
      joinDate: new Date().toISOString().split("T")[0],
      renewalDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      qualifications: [],
      subscriptionStatus: "active",
      unionHistory: [],
    });
  };

  const addQualification = () => {
    if (newQualification.trim()) {
      setFormData({
        ...formData,
        qualifications: [...formData.qualifications, newQualification.trim()],
      });
      setNewQualification("");
    }
  };

  const removeQualification = (index: number) => {
    setFormData({
      ...formData,
      qualifications: formData.qualifications.filter((_, i) => i !== index),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Member" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
          />
          <Input
            label="Phone"
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            required
          />
          <Input
            label="License Number"
            value={formData.licenseNumber}
            onChange={(e) =>
              setFormData({ ...formData, licenseNumber: e.target.value })
            }
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Status
            </label>
            <select
              value={formData.licenseStatus}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  licenseStatus: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Membership Type
            </label>
            <select
              value={formData.membershipType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  membershipType: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="pilot">Pilot</option>
              <option value="retired">Retired</option>
              <option value="honorary">Honorary</option>
            </select>
          </div>
          <Input
            label="Join Date"
            type="date"
            value={formData.joinDate}
            onChange={(e) =>
              setFormData({ ...formData, joinDate: e.target.value })
            }
            required
          />
          <Input
            label="Renewal Date"
            type="date"
            value={formData.renewalDate}
            onChange={(e) =>
              setFormData({ ...formData, renewalDate: e.target.value })
            }
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qualifications
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              value={newQualification}
              onChange={(e) => setNewQualification(e.target.value)}
              placeholder="Add qualification"
              className="flex-1"
            />
            <Button type="button" onClick={addQualification}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.qualifications.map((qual, index) => (
              <Badge
                key={index}
                variant="info"
                className="flex items-center gap-1"
              >
                {qual}
                <button
                  type="button"
                  onClick={() => removeQualification(index)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Add Member</Button>
        </div>
      </form>
    </Modal>
  );
}

// Renewal Modal Component
function RenewalModal({
  member,
  isOpen,
  onClose,
  onRenew,
}: {
  member: Member;
  isOpen: boolean;
  onClose: () => void;
  onRenew: (memberId: string, newRenewalDate: string) => void;
}) {
  const [newRenewalDate, setNewRenewalDate] = useState(
    new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRenew(member.id, newRenewalDate);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Renew Membership" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">
            Member Information
          </h4>
          <p className="text-sm text-gray-600">Name: {member.name}</p>
          <p className="text-sm text-gray-600">
            License: {member.licenseNumber}
          </p>
          <p className="text-sm text-gray-600">
            Current Expiry: {format(new Date(member.renewalDate), "dd/MM/yy")}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New Renewal Date
          </label>
          <Input
            type="date"
            value={newRenewalDate}
            onChange={(e) => setNewRenewalDate(e.target.value)}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Membership will be renewed for one year from this date
          </p>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Renewal Benefits</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Access to all syndicate services</li>
            <li>• Digital ID card update</li>
            <li>• Voting rights in general meetings</li>
            <li>• Legal support and representation</li>
            <li>• Professional development opportunities</li>
          </ul>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            <RefreshCw className="h-4 w-4 mr-2" />
            Renew Membership
          </Button>
        </div>
      </form>
    </Modal>
  );
}
